import { toClientLinkStatusLabel } from "@enrein/shared-contracts/display/tier1-client-display";
import type { V10ClientLinkViewProps, V10ClientTabId } from "./types.js";
import { v10LinkIdFromContext } from "./types.js";
import { resolveClientNextActionHome } from "./adapters/client-next-action-home.js";
import { v10IsIntakePhase } from "./adapters/v10-intake-phase.js";
import { v10DefaultClientTab, v10VisibleClientTabs } from "./adapters/v10-tab-visibility.js";
import { V10ClientFrame, V10ClientTabs } from "./chrome/V10ClientTabs.js";
import { V10SectionProgress } from "./chrome/V10SectionProgress.js";
import { ClarificationWorkspace } from "../../ClarificationWorkspace.js";
import { V10ChangesTab } from "./views/V10ChangesTab.js";
import { V10HandoffTab } from "./views/V10HandoffTab.js";
import { V10IntakeTab } from "./views/V10IntakeTab.js";
import { V10PreviewTab } from "./views/V10PreviewTab.js";
import { V10ProofTab } from "./views/V10ProofTab.js";
import { V10RecordTab } from "./views/V10RecordTab.js";
import { V10SummaryTab } from "./views/V10SummaryTab.js";
import { confirmDesignDirection } from "./adapters/client-design-direction.js";
import { useEffect, useState } from "react";
import "./styles/v10-fonts.css";
import "./styles/v10-client-link.css";

export function V10ClientLinkApp(props: V10ClientLinkViewProps) {
  const tabIds = v10VisibleClientTabs({
    shell: props.shell,
    preview: props.preview,
    isExternalProof: props.isExternalProof,
  });
  const intakePhase = v10IsIntakePhase(props.shell.state.status);
  const canAct = ["PREVIEW_READY", "CHANGE_REQUESTED", "APPROVAL_PENDING"].includes(props.shell.state.status);
  const [activeTab, setActiveTab] = useState<V10ClientTabId>(() =>
    v10DefaultClientTab({ shell: props.shell, tabIds }),
  );
  const [localPreview, setLocalPreview] = useState(props.preview);
  const [approvalBaselineVersionId] = useState(() => props.preview?.previewVersionId ?? null);
  const [directionConfirmed, setDirectionConfirmed] = useState(false);
  const [directionStatus, setDirectionStatus] = useState<string | null>(null);
  const hideDesignDirection =
    directionConfirmed || ["APPROVED", "HANDOFF_READY", "PORTAL_ACTIVE"].includes(props.shell.state.status);
  useEffect(() => {
    setLocalPreview(props.preview);
  }, [props.preview]);
  const statusLabel = toClientLinkStatusLabel(props.shell.state.status);
  const frameSubtitle = intakePhase ? "Business intake" : "Review room";
  const linkId = v10LinkIdFromContext(props.locationContext);
  const intakeComplete = !intakePhase;
  const previewReady =
    Boolean(props.preview?.previewVersionId) ||
    ["PREVIEW_READY", "CHANGE_REQUESTED", "APPROVAL_PENDING", "APPROVED"].includes(props.shell.state.status);
  const nextActionHome = resolveClientNextActionHome({
    projectId: props.shell.clientLinkId,
    clientLinkStatus: props.shell.state.status,
    intakeComplete,
    previewReady,
    journeyPresentation: props.journeyPresentation ?? null,
  });
  useEffect(() => {
    if (!tabIds.includes(activeTab)) {
      setActiveTab(v10DefaultClientTab({ shell: props.shell, tabIds }));
    }
  }, [activeTab, props.shell, tabIds]);

  const handleNextActionClick = () => {
    const intent = nextActionHome.presentation?.primaryAction?.intent;
    if (intent === "OPEN_CLIENT_INTAKE") {
      setActiveTab("intake");
      return;
    }
    if (intent === "OPEN_CLIENT_CLARIFICATION") {
      setActiveTab("requests");
      return;
    }
    if (intent === "OPEN_CLIENT_EXACT_PREVIEW" || intent === "OPEN_CLIENT_REVISED_PREVIEW") {
      setActiveTab("preview");
      return;
    }
    if (intent === "GIVE_EXACT_APPROVAL") {
      setActiveTab("summary");
      return;
    }
    if (intent === "OPEN_OWNERSHIP_RECORDS") {
      setActiveTab("record");
      return;
    }
    if (intent === "OPEN_SAFE_UPDATE") {
      setActiveTab("changes");
    }
  };

  return (
    <div className="v10-cl-wrap">
      <V10ClientFrame
        statusLabel={statusLabel}
        subtitle={frameSubtitle}
        businessDisplayName={props.shell.project.displayName}
      >
        {nextActionHome.presentation ? (
          <section
            className="v10-cl-journey"
            data-testid="client-journey-presentation"
            aria-label="Your next step"
          >
            <h2 className="v10-cl-journey-title" data-testid="client-journey-title">
              {nextActionHome.presentation.title}
            </h2>
            <p className="v10-cl-journey-condition" data-testid="client-journey-condition">
              {nextActionHome.presentation.condition}
            </p>
            {nextActionHome.presentation.relevantRecords.length > 0 ? (
              <ul className="v10-cl-journey-records" data-testid="client-journey-records">
                {nextActionHome.presentation.relevantRecords.map((record) => (
                  <li key={record}>{record}</li>
                ))}
              </ul>
            ) : null}
            <p className="v10-cl-next-action" data-testid="client-next-action">
              {nextActionHome.presentation.primaryAction ? (
                <button
                  type="button"
                  data-testid="client-next-action-cta"
                  onClick={handleNextActionClick}
                >
                  {nextActionHome.nextActionLabel}
                </button>
              ) : (
                nextActionHome.nextActionLabel
              )}
            </p>
          </section>
        ) : (
          <p className="v10-cl-next-action" data-testid="client-next-action">
            {nextActionHome.nextActionLabel}
          </p>
        )}
        {hideDesignDirection ? (
          <section className="v10-cl-design-direction" data-testid="client-design-direction" aria-label="Design direction">
            <h2>Design direction</h2>
            <p>{directionStatus ?? "Design direction is already recorded for this project."}</p>
          </section>
        ) : (
          <section className="v10-cl-design-direction" data-testid="client-design-direction" aria-label="Design direction">
            <h2>Design direction</h2>
            <p>Rank Lens examples, then confirm direction for your agency.</p>
            <ol className="v10-cl-lens-rank">
              <li>Lens Clean — rank 1</li>
              <li>Lens Bold — rank 2</li>
              <li>Lens Warm — rank 3</li>
            </ol>
            <button
              type="button"
              data-testid="confirm-design-direction"
              onClick={() => {
                try {
                  const result = confirmDesignDirection(
                    [
                      { lensId: "lens-clean", rank: 1 },
                      { lensId: "lens-bold", rank: 2 },
                      { lensId: "lens-warm", rank: 3 },
                    ],
                    "lens-clean",
                  );
                  setDirectionConfirmed(true);
                  setDirectionStatus(`Direction confirmed — Lens Clean (rank ${result.rank}).`);
                } catch (error) {
                  setDirectionStatus(error instanceof Error ? error.message : "Could not confirm direction.");
                }
              }}
            >
              Confirm direction
            </button>
            {directionStatus ? <p role="status">{directionStatus}</p> : null}
          </section>
        )}
        <section className="v10-cl-site-health" data-testid="client-site-health" aria-label="Performance and Site Health">
          <h2>Performance / Site Health</h2>
          <p>Track calls, forms, clicks, site health, and change history for your live site.</p>
          <ul>
            <li>Calls</li>
            <li>Forms</li>
            <li>Clicks</li>
            <li>Site Health</li>
            <li>Change history</li>
          </ul>
        </section>
        {!intakePhase && props.shell.sections.length > 0 ? (
          <V10SectionProgress sections={props.shell.sections} />
        ) : null}
        <V10ClientTabs activeTab={activeTab} onTabChange={setActiveTab} tabIds={tabIds} />
        <V10IntakeTab
          active={activeTab === "intake"}
          api={props.api}
          linkId={linkId}
          sessionToken={props.sessionToken}
          linkStatus={props.shell.state.status}
          onStatus={props.onActionStatus}
        />
        <V10SummaryTab
          {...props}
          active={activeTab === "summary"}
          canAct={canAct}
          onApprove={props.onApprove}
          onRequestChange={props.onRequestChange}
        />
        <V10PreviewTab
          {...props}
          preview={localPreview}
          active={activeTab === "preview"}
          approvalBaselineVersionId={approvalBaselineVersionId}
          onPreviewChange={setLocalPreview}
        />
        <V10ProofTab
          active={activeTab === "proof"}
          api={props.api}
          linkId={linkId}
          token={props.sessionToken}
          externalProofView={props.externalProofView}
          afterFixProofSummary={props.afterFixProofSummary}
          isExternalProof={props.isExternalProof}
          onGoToSummary={() => setActiveTab("summary")}
        />
        <V10RecordTab
          active={activeTab === "record"}
          api={props.api}
          linkId={linkId}
          token={props.sessionToken}
          isExternalProof={props.isExternalProof}
        />
        <V10ChangesTab
          {...props}
          active={activeTab === "changes"}
          canAct={canAct}
          onRequestChange={props.onRequestChange}
          shellStatus={props.shell.state.status}
          linkId={linkId}
          token={props.sessionToken}
          preview={localPreview}
          presentationScopeId={props.shell.clientLinkId}
          onOpenRevisedPreview={(previewVersionId) => {
            // Only route to the exact revised-preview surface when the loaded preview matches.
            if (localPreview?.previewVersionId === previewVersionId) {
              setActiveTab("preview");
            }
          }}
        />
        <div
          id="portal-clarifications"
          role="tabpanel"
          aria-labelledby="v10-tab-btn-requests"
          hidden={activeTab !== "requests"}
          data-testid="v10-tab-requests"
        >
          {activeTab === "requests" ? (
            <ClarificationWorkspace
              api={{
                listClarifications: () => props.api.listClarifications(linkId, props.sessionToken),
                respondToClarification: (clarificationRequestId, body) =>
                  props.api.respondToClarification(linkId, props.sessionToken, clarificationRequestId, body),
              }}
            />
          ) : null}
        </div>
        <V10HandoffTab
          active={activeTab === "handoff"}
          api={props.api}
          linkId={linkId}
          token={props.sessionToken}
          deployEnv={props.deployEnv}
        />
        {props.actionStatus ? (
          <p className="v10-cl-action-status" role="status">
            {props.actionStatus}
          </p>
        ) : null}
      </V10ClientFrame>
    </div>
  );
}
